/**
 * The participant's recording notice — the TypeScript port of the consoles'
 * `webui/static/shared/recordingNotice.js` (pure half, plus the panel's DOM).
 *
 * A study's recording policy is per kind (audio, video): off, optional or
 * required (GET /surveys/{id}/recording). What the participant agreed to is
 * stored per interview (GET/POST /interviews/{id}/recording-consent), and only
 * policy *and* consent together turn recording on — the server's `effective`.
 * The widget is voice only, so only the audio row ever appears (modality
 * "voice"), but the model is the consoles' unchanged.
 */

export type Mode = "off" | "optional" | "required";
export type Kind = "audio" | "video";

export interface Policy {
  audio?: Mode | string;
  video?: Mode | string;
}
export interface Consent {
  audio?: boolean | null | string;
  video?: boolean | null | string;
}
export interface Effective {
  audio: boolean;
  video: boolean;
}
export interface NoticeRow {
  kind: Kind;
  mode: "optional" | "required";
  checked: boolean;
  disabled: boolean;
}
export interface NoticeModel {
  rows: NoticeRow[];
  needed: boolean;
  declinable: boolean;
}
export type Draft = Partial<Record<Kind, boolean>>;
export type Translate = (key: string) => string;

const KINDS_FOR: Record<string, Kind[]> = {
  voice: ["audio"],
  voice_local: ["audio"],
  video: ["audio", "video"],
};

const mode = (policy: Policy | null | undefined, kind: Kind): Mode => {
  const m = policy?.[kind];
  return m === "optional" || m === "required" ? m : "off";
};
const decided = (consent: Consent | null | undefined, kind: Kind): boolean =>
  typeof consent?.[kind] === "boolean";

/** Rows re-derived for the checkboxes' current state. Video can only be
 *  recorded with audio: while an optional audio row is unticked, the video row
 *  is unticked and disabled. */
function withDraft(rows: NoticeRow[], draft: Draft): NoticeRow[] {
  const audio = rows.find((r) => r.kind === "audio");
  const audioOn = !audio || audio.mode === "required" || draft.audio === true;
  return rows.map((row) => {
    if (row.mode === "required") return { ...row, checked: true, disabled: true };
    if (row.kind === "video" && !audioOn) return { ...row, checked: false, disabled: true };
    return { ...row, checked: draft[row.kind] === true, disabled: false };
  });
}

/** What to ask: one row per kind this modality uses that the policy records
 *  and the participant hasn't decided. `needed` — anything to ask at all;
 *  `declinable` — a required kind is among them, so "I don't agree" is an
 *  answer (it ends the interview). */
export function noticeModel(
  policy: Policy | null | undefined,
  consent: Consent | null | undefined,
  modality: string,
  draft: Draft = {}
): NoticeModel {
  const rows: NoticeRow[] = [];
  for (const kind of KINDS_FOR[modality] ?? []) {
    const m = mode(policy, kind);
    if (m === "off" || decided(consent, kind)) continue;
    // Audio was declined earlier: video can't be recorded without it.
    if (kind === "video" && consent?.audio === false) continue;
    rows.push({ kind, mode: m, checked: m === "required", disabled: m === "required" });
  }
  return {
    rows: withDraft(rows, draft || {}),
    needed: rows.length > 0,
    declinable: rows.some((r) => r.mode === "required"),
  };
}

/** The POST body for Continue: required rows agree, optional rows send their
 *  checkbox, and video is false whenever audio is. Only shown rows are sent. */
export function noticeChoice(model: NoticeModel, draft: Draft = {}): Draft {
  const choice: Draft = {};
  for (const row of withDraft(model.rows, draft || {})) choice[row.kind] = row.checked;
  if (choice.audio === false && "video" in choice) choice.video = false;
  return choice;
}

/** The banner's sentence for what is actually being recorded, or "". */
export function indicatorText(effective: Effective | null | undefined, t: Translate): string {
  if (!effective?.audio) return "";
  return t(effective.video ? "rec.indicator.video" : "rec.indicator.audio");
}

export interface NoticeAnswer {
  accepted: boolean;
  choice: Draft;
}

let seq = 0;

/** Render the notice into `container` (the widget's splash). Resolves once the
 *  participant answers: accepted on Continue, not accepted on "I don't agree"
 *  (only offered when `model.declinable`). Focus moves to the heading. */
export function mountRecordingNotice(
  container: HTMLElement,
  model: NoticeModel,
  t: Translate
): Promise<NoticeAnswer> {
  const id = `dtroRec${++seq}`;
  const draft: Draft = {};
  const section = document.createElement("section");
  section.className = "dtro-recnotice";
  section.setAttribute("aria-labelledby", `${id}-title`);

  const title = document.createElement("h2");
  title.className = "dtro-recnotice__title";
  title.id = `${id}-title`;
  title.tabIndex = -1;
  title.textContent = t("rec.notice.title");
  section.append(title);

  const inputs: Partial<Record<Kind, HTMLInputElement>> = {};
  let hint: HTMLElement | null = null;
  for (const row of model.rows) {
    if (row.mode === "required") {
      const p = document.createElement("p");
      p.className = "dtro-recnotice__required";
      p.textContent = t(`rec.notice.${row.kind}_required`);
      section.append(p);
      continue;
    }
    const label = document.createElement("label");
    label.className = "dtro-recnotice__option";
    const box = document.createElement("input");
    box.type = "checkbox";
    box.name = `rec-${row.kind}`;
    box.addEventListener("change", () => {
      draft[row.kind] = box.checked;
      // Unticking audio clears video too, so ticking it again doesn't
      // silently bring video back.
      if (row.kind === "audio" && !box.checked) draft.video = false;
      paint();
    });
    const text = document.createElement("span");
    text.textContent = t(`rec.notice.${row.kind}_optional`);
    label.append(box, text);
    section.append(label);
    inputs[row.kind] = box;
    if (row.kind === "video") {
      hint = document.createElement("p");
      hint.className = "dtro-recnotice__hint";
      hint.id = `${id}-video-hint`;
      hint.textContent = t("rec.notice.video_needs_audio");
      box.setAttribute("aria-describedby", hint.id);
      section.append(hint);
    }
  }

  const actions = document.createElement("div");
  actions.className = "dtro-recnotice__actions";
  const go = document.createElement("button");
  go.type = "button";
  go.className = "dtro-btn dtro-recnotice__continue";
  go.textContent = t("rec.notice.continue");
  actions.append(go);
  let no: HTMLButtonElement | null = null;
  if (model.declinable) {
    no = document.createElement("button");
    no.type = "button";
    no.className = "dtro-btn secondary dtro-recnotice__decline";
    no.textContent = t("rec.notice.decline");
    actions.append(no);
  }
  section.append(actions);

  function paint(): void {
    for (const row of withDraft(model.rows, draft)) {
      const box = inputs[row.kind];
      if (!box) continue;
      box.checked = row.checked;
      box.disabled = row.disabled;
      if (row.kind === "video" && hint) hint.hidden = !row.disabled;
    }
  }
  paint();

  container.append(section);
  title.focus();

  return new Promise((resolve) => {
    const done = (answer: NoticeAnswer) => {
      section.remove();
      resolve(answer);
    };
    go.addEventListener("click", () => done({ accepted: true, choice: noticeChoice(model, draft) }));
    no?.addEventListener("click", () => done({ accepted: false, choice: {} }));
  });
}
